import { useState } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Alert,
  AlertTitle,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  HourglassEmpty as HourglassIcon,
  AttachMoney as MoneyIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';

export default function TrialBanner({ trialInfo, companyId }) {
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [generatingPix, setGeneratingPix] = useState(false);
  const [pixData, setPixData] = useState(null);
  const daysRemaining = Math.max(0, Math.ceil(Number(trialInfo?.days_remaining) || 0));

  if (!trialInfo || daysRemaining <= 0) {
    return null;
  }

  const handleGeneratePix = async () => {
    setGeneratingPix(true);
    try {
      const response = await axios.post(`${API}/payments/companies/${companyId}/generate-pix`);
      setPixData(response.data);
      toast.success("QR Code PIX gerado com sucesso!");
    } catch (error) {
      toast.error("Erro ao gerar PIX");
    } finally {
      setGeneratingPix(false);
    }
  };

  const handleCopyPix = () => {
    if (pixData?.qr_code) {
      navigator.clipboard.writeText(pixData.qr_code);
      toast.success("Código PIX copiado!");
    }
  };

  const getSeverity = () => {
    if (daysRemaining <= 3) return 'error';
    if (daysRemaining <= 7) return 'warning';
    return 'info';
  };

  return (
    <>
      <Alert
        severity={getSeverity()}
        icon={<HourglassIcon />}
        sx={{ mb: 3 }}
        action={
          <Button
            color="inherit"
            size="small"
            startIcon={<MoneyIcon />}
            onClick={() => setShowPaymentDialog(true)}
          >
            Pagar Agora
          </Button>
        }
      >
        <AlertTitle>
          <strong>Período de Teste</strong>
        </AlertTitle>
        {`Período de teste: ${daysRemaining} dia${daysRemaining === 1 ? "" : "s"} restantes`} • Valor da mensalidade: <strong>R$ 90,00</strong>
      </Alert>

      {/* Payment Dialog */}
      <Dialog
        open={showPaymentDialog}
        onClose={() => {
          setShowPaymentDialog(false);
          setPixData(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Pagamento Antecipado - Mensalidade
        </DialogTitle>
        <DialogContent>
          {!pixData ? (
            <Box textAlign="center" py={3}>
              <Typography variant="h5" gutterBottom>
                R$ 90,00
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Pagamento mensal via PIX
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={handleGeneratePix}
                disabled={generatingPix}
                startIcon={generatingPix ? <CircularProgress size={20} /> : <MoneyIcon />}
              >
                {generatingPix ? "Gerando..." : "Gerar QR Code PIX"}
              </Button>
            </Box>
          ) : (
            <Box textAlign="center" py={2}>
              <Typography variant="h6" gutterBottom color="success.main">
                QR Code Gerado!
              </Typography>
              
              {pixData.qr_code_base64 && (
                <Box my={3}>
                  <img
                    src={`data:image/png;base64,${pixData.qr_code_base64}`}
                    alt="QR Code PIX"
                    style={{ maxWidth: '300px', width: '100%' }}
                  />
                </Box>
              )}
              
              {pixData.qr_code && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Ou copie o código:
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: 'grey.100',
                      p: 2,
                      borderRadius: 2,
                      wordBreak: 'break-all',
                      fontSize: '0.85rem',
                      fontFamily: 'monospace',
                      maxHeight: 100,
                      overflow: 'auto',
                    }}
                  >
                    {pixData.qr_code}
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CopyIcon />}
                    sx={{ mt: 2 }}
                    onClick={handleCopyPix}
                  >
                    Copiar Código
                  </Button>
                </Box>
              )}

              <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                O pagamento pode levar alguns minutos para ser confirmado
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowPaymentDialog(false);
            setPixData(null);
          }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
